import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { DonationApi } from "../../api/DonationApi";
import type { CreateDonation } from "../../generated/dto/CreateDonation";
import type { CreateDonationFundStaff } from "../../generated/dto/CreateDonationFundStaff";
import type { CreateDonationInitiative } from "../../generated/dto/CreateDonationInitiative";
import type { CreateRecurringDonation } from "../../generated/dto/CreateRecurringDonation";
import type { CsvExportOptions } from "../../generated/dto/CsvExportOptions";
import type { DeleteDonationFundStaff } from "../../generated/dto/DeleteDonationFundStaff";
import type { DeleteDonationInitiativeStaff } from "../../generated/dto/DeleteDonationInitiativeStaff";
import type { DeleteDonationStaff } from "../../generated/dto/DeleteDonationStaff";
import type { DeleteRecurringDonationStaff } from "../../generated/dto/DeleteRecurringDonationStaff";
import type { DonationFetchOptions } from "../../generated/dto/DonationFetchOptions";
import type { DonationFundStaff } from "../../generated/dto/DonationFundStaff";
import type { DonationFundStaffSearchParams } from "../../generated/dto/DonationFundStaffSearchParams";
import type { DonationFundStaffSearchResult } from "../../generated/dto/DonationFundStaffSearchResult";
import type { DonationInitiativeSearchParams } from "../../generated/dto/DonationInitiativeSearchParams";
import type { DonationInitiativeStaff } from "../../generated/dto/DonationInitiativeStaff";
import type { DonationInitiativeStaffSearchResult } from "../../generated/dto/DonationInitiativeStaffSearchResult";
import type { DonationMember } from "../../generated/dto/DonationMember";
import type { DonationMemberFetchOptions } from "../../generated/dto/DonationMemberFetchOptions";
import type { DonationMemberSearchParams } from "../../generated/dto/DonationMemberSearchParams";
import type { DonationMemberSearchResult } from "../../generated/dto/DonationMemberSearchResult";
import type { DonationSearchParams } from "../../generated/dto/DonationSearchParams";
import type { DonationStaff } from "../../generated/dto/DonationStaff";
import type { DonationStaffSearchResult } from "../../generated/dto/DonationStaffSearchResult";
import type { MemberDonationOptions } from "../../generated/dto/MemberDonationOptions";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { RecurringDonationStaff } from "../../generated/dto/RecurringDonationStaff";
import type { UpdateDonation } from "../../generated/dto/UpdateDonation";
import type { UpdateDonationFundStaff } from "../../generated/dto/UpdateDonationFundStaff";
import type { UpdateDonationInitiative } from "../../generated/dto/UpdateDonationInitiative";
import type { UpdateRecurringDonation } from "../../generated/dto/UpdateRecurringDonation";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class DonationApiClient implements DonationApi {
  constructor(private readonly client = useClient()) {}

  async getDonationMember(id: number, options?: DonationMemberFetchOptions): Promise<DonationMember> {
    const { data } = await this.client.get<unknown>(`/donation/member/${id}`, { params: serializeCbiDto(options, "generated.dto.DonationMemberFetchOptions") });
    return deserializeCbiDto<DonationMember>(data, "generated.dto.DonationMember");
  }

  async getDonationStaff(id: number, options?: DonationFetchOptions): Promise<DonationStaff> {
    const { data } = await this.client.get<unknown>(`/donation/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.DonationFetchOptions") });
    return deserializeCbiDto<DonationStaff>(data, "generated.dto.DonationStaff");
  }

  async getRecurringDonationStaff(id: number): Promise<RecurringDonationStaff> {
    const { data } = await this.client.get<unknown>(`/recurringdonation/staff/${id}`);
    return deserializeCbiDto<RecurringDonationStaff>(data, "generated.dto.RecurringDonationStaff");
  }

  async getDonationFundStaff(id: number): Promise<DonationFundStaff> {
    const { data } = await this.client.get<unknown>(`/donationfund/staff/${id}`);
    return deserializeCbiDto<DonationFundStaff>(data, "generated.dto.DonationFundStaff");
  }

  async getDonationInitiativeStaff(id: number): Promise<DonationInitiativeStaff> {
    const { data } = await this.client.get<unknown>(`/donationinitiative/staff/${id}`);
    return deserializeCbiDto<DonationInitiativeStaff>(data, "generated.dto.DonationInitiativeStaff");
  }

  async getAllDonationFundStaffs(): Promise<Array<DonationFundStaff>> {
    const { data } = await this.client.get<unknown>(`/donationfund/staff/all`);
    return deserializeCbiDto<Array<DonationFundStaff>>(data, "generated.dto.DonationFundStaff");
  }

  async getAllDonationInitiativeStaffs(): Promise<Array<DonationInitiativeStaff>> {
    const { data } = await this.client.get<unknown>(`/donationinitiative/staff/all`);
    return deserializeCbiDto<Array<DonationInitiativeStaff>>(data, "generated.dto.DonationInitiativeStaff");
  }

  async createDonationStaff(source: CreateDonation): Promise<DonationStaff> {
    const { data } = await this.client.post<unknown>(`/donation/staff`, serializeCbiDto(source, "generated.dto.CreateDonation"));
    return deserializeCbiDto<DonationStaff>(data, "generated.dto.DonationStaff");
  }

  async createRecurringDonationStaff(source: CreateRecurringDonation): Promise<RecurringDonationStaff> {
    const { data } = await this.client.post<unknown>(`/recurringdonation/staff`, serializeCbiDto(source, "generated.dto.CreateRecurringDonation"));
    return deserializeCbiDto<RecurringDonationStaff>(data, "generated.dto.RecurringDonationStaff");
  }

  async createDonationFundStaff(source: CreateDonationFundStaff): Promise<DonationFundStaff> {
    const { data } = await this.client.post<unknown>(`/donationfund/staff`, serializeCbiDto(source, "generated.dto.CreateDonationFundStaff"));
    return deserializeCbiDto<DonationFundStaff>(data, "generated.dto.DonationFundStaff");
  }

  async createDonationInitiativeStaff(source: CreateDonationInitiative): Promise<DonationInitiativeStaff> {
    const { data } = await this.client.post<unknown>(`/donationinitiative/staff`, serializeCbiDto(source, "generated.dto.CreateDonationInitiative"));
    return deserializeCbiDto<DonationInitiativeStaff>(data, "generated.dto.DonationInitiativeStaff");
  }

  async updateDonationStaff(source: UpdateDonation): Promise<DonationStaff> {
    const { data } = await this.client.put<unknown>(`/donation/staff/${source.donationId}`, serializeCbiDto(source, "generated.dto.UpdateDonation"));
    return deserializeCbiDto<DonationStaff>(data, "generated.dto.DonationStaff");
  }

  async updateRecurringDonationStaff(source: UpdateRecurringDonation): Promise<RecurringDonationStaff> {
    const { data } = await this.client.put<unknown>(`/recurringdonation/staff/${source.rowId}`, serializeCbiDto(source, "generated.dto.UpdateRecurringDonation"));
    return deserializeCbiDto<RecurringDonationStaff>(data, "generated.dto.RecurringDonationStaff");
  }

  async updateDonationFundStaff(source: UpdateDonationFundStaff): Promise<DonationFundStaff> {
    const { data } = await this.client.put<unknown>(`/donationfund/staff/${source.fundId}`, serializeCbiDto(source, "generated.dto.UpdateDonationFundStaff"));
    return deserializeCbiDto<DonationFundStaff>(data, "generated.dto.DonationFundStaff");
  }

  async updateDonationInitiativeStaff(source: UpdateDonationInitiative): Promise<DonationInitiativeStaff> {
    const { data } = await this.client.put<unknown>(`/donationinitiative/staff/${source.initiativeId}`, serializeCbiDto(source, "generated.dto.UpdateDonationInitiative"));
    return deserializeCbiDto<DonationInitiativeStaff>(data, "generated.dto.DonationInitiativeStaff");
  }

  async deleteDonationStaff(source: DeleteDonationStaff): Promise<void> {
    await this.client.delete(`/donation/staff/${source.donationId}`);
  }

  async deleteRecurringDonationStaff(source: DeleteRecurringDonationStaff): Promise<void> {
    await this.client.delete(`/recurringdonation/staff/${source.rowId}`);
  }

  async deleteDonationFundStaff(source: DeleteDonationFundStaff): Promise<void> {
    await this.client.delete(`/donationfund/staff/${source.fundId}`);
  }

  async deleteDonationInitiativeStaff(source: DeleteDonationInitiativeStaff): Promise<void> {
    await this.client.delete(`/donationinitiative/staff/${source.initiativeId}`);
  }

  async searchDonationMember(params: DonationMemberSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationMemberSearchResult>> {
    const { data } = await this.client.post<unknown>(`/donation/member/search`, serializeCbiDto(params, "generated.dto.DonationMemberSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DonationMemberSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<DonationMemberSearchResult>>(result.items, "generated.dto.DonationMemberSearchResult") };
    return result;
  }

  async searchDonationStaff(params: DonationSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/donation/staff/search`, serializeCbiDto(params, "generated.dto.DonationSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DonationStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<DonationStaffSearchResult>>(result.items, "generated.dto.DonationStaffSearchResult") };
    return result;
  }

  async exportDonationStaffSearchToCsv(params: DonationSearchParams, options?: CsvExportOptions): Promise<string> {
    const { data } = await this.client.get<unknown>(`/donation/staff/search/csv`, { params: { params: encodeRequestParam(params, "generated.dto.DonationSearchParams"), options: options === undefined ? undefined : encodeRequestParam(options, "generated.dto.CsvExportOptions") } });
    return data as string;
  }

  async searchDonationFundStaff(params: DonationFundStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationFundStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/donationfund/staff/search`, serializeCbiDto(params, "generated.dto.DonationFundStaffSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DonationFundStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<DonationFundStaffSearchResult>>(result.items, "generated.dto.DonationFundStaffSearchResult") };
    return result;
  }

  async searchDonationInitiativeStaff(params: DonationInitiativeSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationInitiativeStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/donationinitiative/staff/search`, serializeCbiDto(params, "generated.dto.DonationInitiativeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DonationInitiativeStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<DonationInitiativeStaffSearchResult>>(result.items, "generated.dto.DonationInitiativeStaffSearchResult") };
    return result;
  }

  async searchDonationMemberFull(params: DonationMemberSearchParams, page: number, perPage: number, options?: DonationMemberFetchOptions): Promise<PaginationResult<DonationMember>> {
    const { data } = await this.client.post<unknown>(`/donation/member/search/full`, serializeCbiDto(params, "generated.dto.DonationMemberSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.DonationMemberFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<DonationMember>;
    result = { ...result, items: deserializeCbiDto<Array<DonationMember>>(result.items, "generated.dto.DonationMember") };
    return result;
  }

  async searchDonationStaffFull(params: DonationSearchParams, page: number, perPage: number, options?: DonationFetchOptions): Promise<PaginationResult<DonationStaff>> {
    const { data } = await this.client.post<unknown>(`/donation/staff/search/full`, serializeCbiDto(params, "generated.dto.DonationSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.DonationFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<DonationStaff>;
    result = { ...result, items: deserializeCbiDto<Array<DonationStaff>>(result.items, "generated.dto.DonationStaff") };
    return result;
  }

  async searchDonationFundStaffFull(params: DonationFundStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationFundStaff>> {
    const { data } = await this.client.post<unknown>(`/donationfund/staff/search/full`, serializeCbiDto(params, "generated.dto.DonationFundStaffSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DonationFundStaff>;
    result = { ...result, items: deserializeCbiDto<Array<DonationFundStaff>>(result.items, "generated.dto.DonationFundStaff") };
    return result;
  }

  async searchDonationInitiativeStaffFull(params: DonationInitiativeSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationInitiativeStaff>> {
    const { data } = await this.client.post<unknown>(`/donationinitiative/staff/search/full`, serializeCbiDto(params, "generated.dto.DonationInitiativeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<DonationInitiativeStaff>;
    result = { ...result, items: deserializeCbiDto<Array<DonationInitiativeStaff>>(result.items, "generated.dto.DonationInitiativeStaff") };
    return result;
  }

  async getDonationStaffFundId(id: number): Promise<DonationFundStaff> {
    const { data } = await this.client.get<unknown>(`/donation/staff/${id}/fundid`);
    return deserializeCbiDto<DonationFundStaff>(data, "generated.dto.DonationFundStaff");
  }

  async getDonationStaffInitiativeId(id: number): Promise<DonationInitiativeStaff> {
    const { data } = await this.client.get<unknown>(`/donation/staff/${id}/initiativeid`);
    return deserializeCbiDto<DonationInitiativeStaff>(data, "generated.dto.DonationInitiativeStaff");
  }

  async getDonationStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/donation/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async purchaseOptionsMember(): Promise<MemberDonationOptions> {
    const { data } = await this.client.post<unknown>(`/donation/member/purchaseoptions`);
    return deserializeCbiDto<MemberDonationOptions>(data, "generated.dto.MemberDonationOptions");
  }

  async purchaseOptionsPublic(): Promise<MemberDonationOptions> {
    const { data } = await this.client.post<unknown>(`/donation/public/purchaseoptions`);
    return deserializeCbiDto<MemberDonationOptions>(data, "generated.dto.MemberDonationOptions");
  }
}

export const useDonationApi = (): DonationApi => new DonationApiClient();
